import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

/**
 * Rozwijana lista z wyszukiwaniem zamiast systemowego `<select>`.
 *
 * PO CO: systemowa lista wygląda różnie w każdej przeglądarce i nie da się w niej
 * wpisać fragmentu nazwy. Przy 30 producentach i 60 mocach wpisanie „47” jest
 * szybsze niż przewijanie (prośba właściciela 2026-09-28).
 *
 * Obsługa jak w comboboxie: strzałki, Enter, Escape, wpisywanie od razu na przycisku
 * otwiera listę z tą literą w polu wyszukiwania. Kliknięcie poza listą ją zamyka.
 *
 * `opcje`: [{ value, label, licznik? }]. `pusta`: podpis opcji „nie wiem”, która czyści
 * wybór; bez niej wyboru nie da się cofnąć. Pole wyszukiwania pokazujemy od 7 opcji.
 */

// „Łódź” i „lodz” mają się znaleźć tak samo: bez polskich znaków i wielkości liter.
const znormalizuj = (t) => String(t).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l')

/** Pogrubia dopasowany fragment. Pozycja z wersji znormalizowanej, bo ma tę samą długość. */
const Podswietl = ({ tekst, fraza }) => {
  if (!fraza) return tekst
  const i = znormalizuj(tekst).indexOf(znormalizuj(fraza))
  if (i < 0) return tekst
  return (
    <>
      {tekst.slice(0, i)}
      <mark className="bg-transparent font-bold text-blue-700">{tekst.slice(i, i + fraza.length)}</mark>
      {tekst.slice(i + fraza.length)}
    </>
  )
}

const Rozwijacz = ({
  wartosc, opcje, onZmiana, etykieta, placeholder = 'Wybierz', pusta, disabled = false,
  szukajPlaceholder = 'Wpisz, żeby zawęzić', ariaLabel,
}) => {
  const [otwarty, setOtwarty] = useState(false)
  const [fraza, setFraza] = useState('')
  const [podswietlony, setPodswietlony] = useState(0)
  const korzen = useRef(null)
  const przycisk = useRef(null)
  const pole = useRef(null)
  const lista = useRef(null)
  const idListy = useId()

  const zSzukaniem = opcje.length >= 7
  const wybrana = opcje.find((o) => String(o.value) === String(wartosc))

  // Opcja „nie wiem” zawsze na górze, także przy wpisanej frazie: to ona cofa wybór.
  const widoczne = useMemo(() => {
    const f = znormalizuj(fraza.trim())
    const pasujace = f ? opcje.filter((o) => znormalizuj(o.label).includes(f)) : opcje
    return pusta ? [{ value: '', label: pusta, pusta: true }, ...pasujace] : pasujace
  }, [opcje, fraza, pusta])

  const otworz = (poczatek = '') => {
    if (disabled) return
    setFraza(poczatek)
    setOtwarty(true)
  }
  const zamknij = (wrocDoPrzycisku = true) => {
    setOtwarty(false)
    setFraza('')
    if (wrocDoPrzycisku) przycisk.current?.focus()
  }
  const wybierz = (o) => {
    onZmiana(o.value)
    zamknij()
  }

  // Po otwarciu: fokus w polu wyszukiwania i podświetlenie wybranej opcji (albo pierwszej pasującej).
  useEffect(() => {
    if (!otwarty) return
    if (zSzukaniem) pole.current?.focus()
    else lista.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otwarty])
  useEffect(() => {
    if (!otwarty) return
    const i = fraza ? widoczne.findIndex((o) => !o.pusta) : widoczne.findIndex((o) => String(o.value) === String(wartosc ?? ''))
    setPodswietlony(Math.max(0, i))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otwarty, fraza])

  // Podświetlona opcja zawsze w widoku listy.
  useEffect(() => {
    lista.current?.querySelector(`[data-indeks="${podswietlony}"]`)?.scrollIntoView?.({ block: 'nearest' })
  }, [podswietlony])

  // Klik poza komponentem zamyka listę bez zmiany wyboru.
  useEffect(() => {
    if (!otwarty) return undefined
    const poza = (e) => { if (!korzen.current?.contains(e.target)) zamknij(false) }
    document.addEventListener('mousedown', poza)
    return () => document.removeEventListener('mousedown', poza)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otwarty])

  const klawisze = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setPodswietlony((i) => Math.min(widoczne.length - 1, i + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setPodswietlony((i) => Math.max(0, i - 1)) }
    else if (e.key === 'Home') { e.preventDefault(); setPodswietlony(0) }
    else if (e.key === 'End') { e.preventDefault(); setPodswietlony(widoczne.length - 1) }
    else if (e.key === 'Enter') { e.preventDefault(); if (widoczne[podswietlony]) wybierz(widoczne[podswietlony]) }
    else if (e.key === 'Escape') { e.preventDefault(); zamknij() }
    else if (e.key === 'Tab') zamknij(false)
  }

  const klawiszePrzycisku = (e) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); otworz() }
    // Pisanie na zamkniętym przycisku od razu szuka: „D” otwiera listę z „D” w polu.
    else if (zSzukaniem && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); otworz(e.key) }
  }

  const aktywnaId = otwarty && widoczne[podswietlony] ? `${idListy}-${podswietlony}` : undefined

  return (
    <div ref={korzen} className="relative">
      <button ref={przycisk} type="button" disabled={disabled}
              onClick={() => (otwarty ? zamknij() : otworz())} onKeyDown={klawiszePrzycisku}
              aria-haspopup="listbox" aria-expanded={otwarty} aria-controls={idListy} aria-label={ariaLabel || etykieta}
              className={`group w-full flex items-center gap-2 rounded-xl border-2 px-3 text-left transition
                ${etykieta ? 'py-1.5' : 'py-2.5'}
                ${otwarty ? 'border-blue-500 bg-white ring-4 ring-blue-100 dark:ring-blue-500/25' : wybrana ? 'border-blue-300 bg-white hover:border-blue-400' : 'border-blue-200 bg-blue-50/40 hover:border-blue-300'}
                focus:outline-none focus-visible:border-blue-500 focus-visible:ring-4 focus-visible:ring-blue-100 dark:focus-visible:ring-blue-500/25
                disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-blue-200`}>
        <span className="min-w-0 flex-1">
          {etykieta && <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400">{etykieta}</span>}
          <span className={`block truncate text-sm ${wybrana ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
            {wybrana ? wybrana.label : placeholder}
          </span>
        </span>
        <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform group-hover:text-blue-500 ${otwarty ? 'rotate-180 text-blue-500' : ''}`} />
      </button>

      {otwarty && (
        <div className="absolute left-0 right-0 z-40 mt-2 min-w-[200px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5">
          {zSzukaniem && (
            <div className="flex items-center gap-2 border-b border-gray-100 px-3">
              <MagnifyingGlassIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
              <input ref={pole} type="text" value={fraza} onChange={(e) => setFraza(e.target.value)} onKeyDown={klawisze}
                     placeholder={szukajPlaceholder} aria-label={szukajPlaceholder}
                     role="combobox" aria-expanded="true" aria-controls={idListy} aria-activedescendant={aktywnaId}
                     className="w-full bg-transparent py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none" />
            </div>
          )}
          <ul ref={lista} id={idListy} role="listbox" tabIndex={-1} onKeyDown={zSzukaniem ? undefined : klawisze}
              aria-activedescendant={zSzukaniem ? undefined : aktywnaId}
              className="max-h-64 overflow-y-auto overscroll-contain py-1 [scrollbar-width:thin] focus:outline-none">
            {widoczne.map((o, i) => {
              const zaznaczona = !o.pusta && String(o.value) === String(wartosc)
              return (
                <li key={`${o.value}-${i}`} id={`${idListy}-${i}`} data-indeks={i} role="option" aria-selected={zaznaczona}
                    onMouseEnter={() => setPodswietlony(i)}
                    onMouseDown={(e) => e.preventDefault()} onClick={() => wybierz(o)}
                    className={`mx-1 flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm
                      ${i === podswietlony ? 'bg-blue-50' : ''}
                      ${o.pusta ? 'text-gray-500' : zaznaczona ? 'font-semibold text-blue-700' : 'text-gray-800'}`}>
                  <span className="min-w-0 flex-1 truncate">{o.pusta ? o.label : <Podswietl tekst={o.label} fraza={fraza.trim()} />}</span>
                  {o.licznik != null && <span className="text-[11px] tabular-nums text-gray-400">{o.licznik}</span>}
                  {zaznaczona && <CheckIcon className="w-4 h-4 flex-shrink-0 text-blue-600" />}
                </li>
              )
            })}
            {widoczne.filter((o) => !o.pusta).length === 0 && (
              <li className="px-3 py-2.5 text-xs text-gray-500">Nic nie pasuje do „{fraza.trim()}”.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

export default Rozwijacz
